import { useState } from 'react'
// @ts-ignore
import classnames from 'classnames'
import { useQuery } from '@tanstack/react-query'
import { omit } from 'lodash'
import { Col, Row } from 'react-bootstrap'
import { Modal, ModalBody } from 'components/modal'
import Link from 'components/link'
import Image from 'components/image'
// @ts-ignore
import Quantity from 'components/quantity'
// @ts-ignore
import AddToCartButton from 'store/components/add-to-cart-button'
// @ts-ignore
import AddToRegistryButton from 'store/components/add-to-registry-button'
// @ts-ignore
import { addToBabylist, getProduct } from 'store/api'
import Loader from 'components/Loader'
import { successMessage } from 'lib/flash-message'
import { pdpPath } from 'lib/urls'
import SelectionControls from '../product-details-page/selection-controls'
import ProductInfo from '../product-details-page/product-info'
import { parseErrorMessage } from 'lib/api-utils'

interface QuickViewModalProps {
  productId: number
  onHide(): void
  show: boolean
  onAddtoCartSuccess: any
  showAddToRegistryButton?: boolean
}

const QuickViewModal = ({
  productId,
  onHide,
  show,
  onAddtoCartSuccess = () => {},
  showAddToRegistryButton = false,
}: QuickViewModalProps) => {
  const { data, isLoading } = useQuery({
    queryKey: [productId],
    queryFn: () => getProduct(productId),
    refetchOnWindowFocus: false,
  })

  const handleOnAddToCartSuccess = () => {
    onAddtoCartSuccess()
    successMessage(`${data?.genericProduct?.shortName} added to cart!`)
    onHide()
  }

  return (
    <Modal show={show} onHide={onHide} title=" " size="small">
      <ModalBody>
        {!isLoading && data?.genericProduct && (
          <QuickViewModalContent
            genericProduct={data.genericProduct}
            onAddToCartSuccess={handleOnAddToCartSuccess}
            showAddToRegistryButton={showAddToRegistryButton}
          />
        )}
        {isLoading && (
          <div
            className="flex col align-center"
            style={{ minHeight: 260, width: '100%' }}
          >
            <Loader className="center-xy" style={undefined} />
          </div>
        )}
      </ModalBody>
    </Modal>
  )
}

export const QuickViewModalContent = ({
  genericProduct,
  onAddToCartSuccess,
  showAddToRegistryButton,
}: any) => {
  const {
    activeProductId,
    attributes,
    brand,
    id,
    name,
    shortName,
    slug,
    products,
    productAttributeMap,
  } = genericProduct
  const [activeProduct, setActiveProduct] = useState(
    products[activeProductId] || null
  )
  const [quantity, setQuantity] = useState(1)
  const showQuantityLimit = activeProduct?.showPurchaseQuantityLimit || false
  const quantityLimit = activeProduct?.purchaseQuantityLimit || 10
  const productLinkUrl = pdpPath(slug, id, activeProduct.id)
  const disableAddToCart =
    !activeProduct.availableOnBabylist ||
    activeProduct.isCashFund ||
    activeProduct.isFavor

  const productPath = (productId: number) => pdpPath(slug, id, productId)
  const pdpLinkDecorator = (child: any) => (
    <Link className="text-body" url={productLinkUrl} newWindow>
      {child}
    </Link>
  )

  const showQuantitySelector =
    !activeProduct.isGiftCard &&
    !activeProduct.isCashFund &&
    !activeProduct.isCrowdfund

  const addToRegistryAction = (registryId: number): Promise<any> => {
    return addToBabylist({
      quantity,
      product_id: activeProduct.id,
      registryId: registryId,
    }).catch((resp: any) => {
      PubSub.publish(BLConstants.FLASH_MESSAGE, {
        message: parseErrorMessage(resp),
        variant: 'danger',
      })
    })
  }

  return (
    <Row>
      <Col md={6}>
        {pdpLinkDecorator(
          <Image src={activeProduct?.images[0]?.url} width="100%" />
        )}
      </Col>
      <Col md={6}>
        <ProductInfo
          name={activeProduct.isElectronicGiftCard ? name : shortName}
          brand={brand}
          callouts={activeProduct.callouts}
          isElectronicGiftCard={activeProduct.isElectronicGiftCard}
          merchandisingMessage={activeProduct.merchandisingMessage || ''}
          price={activeProduct.price}
          productId={activeProduct.id}
          productType={activeProduct.productType}
          reviewSku={activeProduct.turntoSku}
          showPrice
          showInStockNotificationLink={false}
          surchargeModalPrompt={null}
          extendedDeliveryTimeMessage={null}
          reactRouterEnabled={false}
          titleDecorator={pdpLinkDecorator}
        />
        <SelectionControls
          allAttributes={attributes}
          attributeLabelOverrides={
            activeProduct.isCashFund ? { amount: 'goal' } : null
          }
          /* eslint-disable-next-line react/jsx-no-bind */
          onProductChange={(productId: any) =>
            setActiveProduct(products[productId])
          }
          productAttributeMap={productAttributeMap}
          products={products}
          productPath={productPath}
          selectedAttributes={omit(activeProduct.attributes, 'sizeOnCatalog')}
        />
        {showQuantitySelector && (
          <Quantity
            className="mtm mbl"
            min={1}
            max={quantityLimit}
            onChange={(qty: number) => setQuantity(qty)}
            limit={showQuantityLimit ? quantityLimit : null}
            value={quantity}
          />
        )}
        {showAddToRegistryButton && (
          <AddToRegistryButton
            productId={activeProduct.id}
            quantity={quantity}
            useInfoButtonStyle
            buttonStyle={{ pill: true, block: true }}
            style={undefined}
            onSuccess={() => {}}
            addToRegistry={addToRegistryAction}
          />
        )}
        <AddToCartButton
          productId={activeProduct.id}
          quantity={quantity}
          useInfoButtonStyle
          buttonStyle={{
            pill: true,
            block: true,
            empty: showAddToRegistryButton,
          }}
          style={undefined}
          onSuccess={onAddToCartSuccess}
          disabled={disableAddToCart}
        />
      </Col>
    </Row>
  )
}

export default QuickViewModal
